import { PropsWithChildren } from 'react';
import Image from 'next/image';
import Cookies from 'js-cookie';

import {
  Box,
  Button,
  createIcon,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Heading,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { Link } from '~/components/link';
import { useMastersPrice } from '~/features/accreditation/hooks';
import { trackEvent } from '~/features/analytics/track-event';
import { TEAM_QUANTITY_MIN } from '~/features/checkout/constants';
import { BillingFrequency } from '~/features/checkout/models/checkout';
import {
  SUBSCRIPTION_REFERRER_PROGRAM_KEY_COOKIE,
  SUBSCRIPTION_REFERRER_PROGRAM_TITLE,
  SUBSCRIPTION_REFERRER_URL_COOKIE,
} from '~/features/cookies/constants';
import { useClientExperiment } from '~/features/experiments/hooks/use-client-experiment';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { PlanId } from '~/features/plans/models/plan-metadata';
import { useUser } from '~/features/user/hooks/use-user';
import { useClientRender } from '~/hooks/use-client-render';

import { BookIcon } from '../icons';

export type SignUpButtonProps = PropsWithChildren<{
  billingFrequency: BillingFrequency;
  planId: PlanId;
  overrideLabel?: string;
  ariaLabel?: string;
  disabled?: boolean;
  onClick?: () => void;
  programKey?: string;
  programTitle?: string;
  colorScheme?: 'blue' | 'lime';
}>;

const buttonStyleProps = {
  width: '100%',
};

function SignUpButtonBase({
  overrideLabel,
  ariaLabel,
  href,
  disabled,
  onClick,
  children,
  colorScheme = 'blue',
}: Pick<SignUpButtonProps, 'overrideLabel' | 'ariaLabel' | 'disabled' | 'onClick' | 'children' | 'colorScheme'> & {
  href: string;
}) {
  const label = overrideLabel ?? 'Enroll Now';
  return (
    <ButtonLink
      buttonProps={{
        ...buttonStyleProps,
        'aria-label': ariaLabel,
        isDisabled: disabled,
        onClick,
        colorScheme,
      }}
      linkProps={{
        href,
      }}
    >
      {children || label}
    </ButtonLink>
  );
}

function WoolfMastersDrawerButton({
  buttonLabel,
  programTitle,
  programKey,
  billingFrequency,
}: {
  buttonLabel: string;
  programTitle: string;
  programKey: string;
  billingFrequency: BillingFrequency;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure({
    onOpen: () =>
      trackEvent({
        name: 'Banner Viewed',
        type: 'view',
        label: 'smart_shelf_viewed',
      }),
  });
  const { currency } = useCurrency();
  const { data: mastersPrice } = useMastersPrice(currency);
  const placement = useBreakpointValue(
    {
      base: 'bottom',
      md: 'right',
    },
    { fallback: 'md' }
  );

  const handleEnrollNowClick = (label: string) => {
    // Note: these cookies are required for downstream services from checkout.
    Cookies.set(SUBSCRIPTION_REFERRER_URL_COOKIE, window.location.pathname, { expires: 1 });
    Cookies.set(SUBSCRIPTION_REFERRER_PROGRAM_KEY_COOKIE, programKey, { expires: 1 });
    Cookies.set(SUBSCRIPTION_REFERRER_PROGRAM_TITLE, programTitle, { expires: 1 });
    trackEvent({
      type: 'click',
      name: 'Product Page CTA Clicked',
      label,
    });
  };

  return (
    <>
      <Button width='full' onClick={onOpen}>
        {buttonLabel}
      </Button>
      <Drawer isOpen={isOpen} placement={placement as any} variant='woolfMasters' blockScrollOnMount onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent
          p={{
            md: 2,
          }}
        >
          <DrawerBody padding={0}>
            <Flex
              backdropFilter='blur(16px)'
              bgColor='white'
              borderRadius='8px'
              flexDirection='column'
              overflow='hidden'
              paddingBottom='24px'
              paddingX={6}
              position='relative'
              width='100%'
              gap={{
                base: '24px',
                md: '40px',
              }}
              paddingTop={{
                base: '26px',
                md: '152px',
              }}
            >
              <Box
                left='8px'
                position='absolute'
                top='0'
                display={{
                  base: 'none',
                  md: 'block',
                }}
              >
                <Image
                  alt=''
                  height={120}
                  loading='eager'
                  src='/images/woolf-badge.png'
                  width={79.6}
                  style={{
                    pointerEvents: 'none',
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <Flex flexDirection='column' gap={6}>
                <Flex
                  alignItems='flex-start'
                  borderBottom={{
                    md: '1px solid',
                  }}
                  borderBottomColor={{
                    md: 'grey.200',
                  }}
                  flexBasis={{
                    md: '80px',
                  }}
                  marginBottom={{
                    base: '-16px',
                    md: 'unset',
                  }}
                >
                  <Box
                    flexShrink={0}
                    left='-16px'
                    position='relative'
                    top='-26px'
                    display={{
                      base: 'block',
                      md: 'none',
                    }}
                  >
                    <Image
                      alt=''
                      height={108}
                      loading='eager'
                      src='/images/woolf-badge.png'
                      width={72}
                      style={{
                        pointerEvents: 'none',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                  <Heading
                    as='h2'
                    fontWeight='600'
                    whiteSpace='pre-wrap'
                    borderBottom={{
                      base: '1px solid',
                      md: 'unset',
                    }}
                    borderBottomColor={{
                      base: 'grey.200',
                      md: 'unset',
                    }}
                    flex={{
                      base: 1,
                      md: 'unset',
                    }}
                    fontSize={{
                      base: '1.375rem',
                      md: '1.5rem',
                    }}
                    lineHeight={{
                      base: '1.2',
                      md: '1.14',
                    }}
                    paddingBottom={{
                      base: '16px',
                      md: 'unset',
                    }}
                  >{`Earn academic credit\nwhile you learn`}</Heading>
                </Flex>
                <Text color='grey.500'>
                  The time you spend learning 
                  <Box as='b' color='grey.900' fontWeight='600'>
                    {programTitle}
                  </Box>
                   can earn you transferable academic credit that counts towards a 
                  <Box as='b' color='grey.900' fontWeight='600'>
                    master&apos;s degree in AI
                  </Box>
                  .
                </Text>
              </Flex>
              <Flex flexDirection='column' gap={4}>
                <Heading
                  alignItems='center'
                  color='blue.700'
                  display='flex'
                  fontSize='0.8125rem'
                  fontWeight='600'
                  gap={3}
                  justifyContent='center'
                  lineHeight='1.4'
                >
                  <MastersAIIcon height='11px' width='11px' />
                  One-time master&apos;s enrollment fee
                </Heading>
                <ButtonLink
                  buttonProps={{
                    onClick: () => {
                      handleEnrollNowClick('master_enroll_now');
                    },
                  }}
                  linkProps={{
                    href: `/checkout/individual/${billingFrequency}/credit-card?degreeApplication=true`,
                  }}
                >
                  Enroll for {mastersPrice}
                </ButtonLink>
                <ButtonLink
                  buttonProps={{
                    variant: 'outline',
                    colorScheme: 'blue',
                    onClick: () => {
                      handleEnrollNowClick('master_dismiss');
                    },
                  }}
                  linkProps={{
                    href: `/checkout/individual/${billingFrequency}/credit-card`,
                  }}
                >
                  Dismiss
                </ButtonLink>
              </Flex>
              <Flex
                alignItems='center'
                borderTop='1px solid'
                borderTopColor='grey.200'
                gap={2}
                justifyContent='center'
                paddingTop={6}
                flexBasis={{
                  md: '42px',
                }}
              >
                <BookIcon height='16px' width='16px' />
                <Link
                  color='grey.900 !important'
                  fontWeight='400'
                  href='/masters-artificial-intelligence'
                  isExternal
                  onClick={() =>
                    trackEvent({
                      type: 'click',
                      name: 'Product Page CTA Clicked',
                      label: 'master_learn_more',
                    })
                  }
                >
                  Learn more about master&apos;s degree
                </Link>
              </Flex>
            </Flex>
          </DrawerBody>
          <DrawerCloseButton
            onClick={() =>
              trackEvent({
                type: 'click',
                name: 'Product Page CTA Clicked',
                label: 'master_close',
              })
            }
          />
        </DrawerContent>
      </Drawer>
    </>
  );
}

const MastersAIIcon = createIcon({
  displayName: 'MastersAIIcon',
  viewBox: '0 0 20 20',
  path: (
    <>
      <g clipPath='url(#clip0_484_2)'>
        <path d='M20 20H0L8.74234e-07 0L20 20Z' fill='#915B14' opacity='0.3' />
        <path d='M0 20H20V0L0 20Z' fill='#915B14' opacity='0.5' />
      </g>
      <defs>
        <clipPath id='clip0_484_2'>
          <rect fill='white' height='20' width='20' />
        </clipPath>
      </defs>
    </>
  ),
});

function useWoolfDrawerABTest() {
  return useClientExperiment('ab_woolf_drawer', {
    pathRegex:
      /^\/course\/(data-analyst-nanodegree--nd002|intro-to-programming-nanodegree--nd000|deep-learning-nanodegree--nd101|ai-programming-python-nanodegree--nd089|digital-marketing-nanodegree--nd018|data-engineer-nanodegree--nd027|business-analytics-nanodegree--nd098|programming-for-data-science-nanodegree--nd104|ios-developer-nanodegree--nd003|data-scientist-nanodegree--nd025|product-manager-nanodegree--nd036|react-nanodegree--nd019|backend-developer-with-python--nd0044|data-structures-and-algorithms-nanodegree--nd256|front-end-web-developer-nanodegree--nd0011|ai-for-trading--nd880|ux-designer-nanodegree--nd578|cloud-dev-ops-nanodegree--nd9991|computer-vision-nanodegree--nd891|cloud-developer-nanodegree--nd9990|ai-product-manager-nanodegree--nd088|deep-reinforcement-learning-nanodegree--nd893|intro-to-self-driving-cars--nd113|robotics-software-engineer--nd209|java-developer-nanodegree--nd035|learn-sql--nd072|intro-to-machine-learning-nanodegree--nd229|intro-to-machine-learning-with-tensorflow-nanodegree--nd230|ai-artificial-intelligence-nanodegree--nd898|predictive-analytics-for-business-nanodegree--nd008t|aws-cloud-architect-nanodegree--nd063|ai-for-business-leaders--nd054|data-analysis-and-visualization-with-power-BI-nanodegree--nd331|agentic-ai--nd900|intermediate-python-nanodegree--nd303|sensor-fusion-engineer-nanodegree--nd313|data-product-manager-nanodegree--nd030|marketing-analytics-nanodegree--nd028|aws-machine-learning-engineer-nanodegree--nd189|data-visualization-nanodegree--nd197|data-streaming-nanodegree--nd029|intro-to-cybersecurity-nanodegree--nd545|android-kotlin-developer-nanodegree--nd940|machine-learning-dev-ops-engineer-nanodegree--nd0821|java-programming-nanodegree--nd079|ai-for-healthcare-nanodegree--nd320|flying-car-nanodegree--nd787|data-architect-nanodegree--nd038|self-driving-car-engineer-nanodegree--nd0013|full-stack-javascript-developer-nanodegree--nd0067|agile-software-development-nanodegree--nd144|intermediate-javascript-nanodegree--nd032|digital-transformation-for-business-leaders--nd055|ai-trading-strategies--nd881|growth-product-manager-nanodegree--nd037|digital-project-management-nanodegree--nd043|cloud-developer-using-microsoft-azure-nanodegree--nd081|machine-learning-engineer-for-microsoft-azure-nanodegree--nd00333|cloud-devops-using-microsoft-azure-nanodegree--nd082|data-engineering-with-microsoft-azure-nanodegree--nd0277|ethical-hacker-nanodegree--nd350|security-engineer-nanodegree--nd698|programming-for-data-science-nanodegree-with-R--nd118|cloud-architect-using-microsoft-azure-nanodegree--nd090|statistics-for-data-analysis-nanodegree--nd588|site-reliability-engineer-nanodegree--nd087|ai-engineer-using-microsoft-azure--nd073|security-analyst-nanodegree--nd324|security-architect-nanodegree--nd992|cloud-computing-for-business-leaders-nanodegree--nd046|azure-generative-ai-engineer--nd444|cybersecurity-for-business-leaders--nd1202|enterprise-security-nanodegree--nd0035|digital-arts-foundation--nd549|google-analytics-four--nd525|backend-with-postgres-and-go--nd810|machine-learning-model-optimization--nd446)$/,
  });
}

export function UnauthenticatedSignUpButton(props: SignUpButtonProps) {
  const { isLoading: isClientExperimentLoading, variant } = useWoolfDrawerABTest();
  const { overrideLabel, planId, billingFrequency } = props;
  const search = new URLSearchParams();
  if (planId === 'individual' && billingFrequency === 'purchase' && !!props.programKey) {
    search.set('nodeKey', props.programKey);
  }
  const params = search.toString();
  const label = overrideLabel ?? 'Enroll Now';

  if (variant === 'variant_b' && (billingFrequency === '4month' || billingFrequency === 'monthly')) {
    return (
      <WoolfMastersDrawerButton
        billingFrequency={billingFrequency}
        buttonLabel={label}
        programKey={props.programKey!}
        programTitle={props.programTitle!}
      />
    );
  }

  return (
    <SignUpButtonBase
      {...props}
      disabled={props?.disabled || isClientExperimentLoading}
      href={`/checkout/${planId}/${billingFrequency}/credit-card${params ? `?${params}` : ''}`}
    >
      {label}
    </SignUpButtonBase>
  );
}

export function AuthenticatedSignUpButton(props: SignUpButtonProps) {
  const { isLoading: isClientExperimentLoading, variant } = useWoolfDrawerABTest();
  const { overrideLabel, planId, billingFrequency } = props;
  const programKey = props.programKey;

  const search = new URLSearchParams();
  if (planId.includes('team')) {
    search.set('quantity', String(TEAM_QUANTITY_MIN));
  }
  if (planId === 'individual' && billingFrequency === 'purchase' && !!programKey) {
    search.set('nodeKey', programKey);
  }
  const params = search.toString();
  const label = overrideLabel ?? 'Enroll Now';

  if (variant === 'variant_b' && (billingFrequency === '4month' || billingFrequency === 'monthly')) {
    return (
      <WoolfMastersDrawerButton
        billingFrequency={billingFrequency}
        buttonLabel={label}
        programKey={programKey!}
        programTitle={props.programTitle!}
      />
    );
  }

  return (
    <SignUpButtonBase
      {...props}
      href={`/checkout/${planId}/${billingFrequency}/credit-card${params ? `?${params}` : ''}`}
      disabled={
        props?.disabled ||
        (planId === 'individual' && billingFrequency === 'purchase' && !programKey) ||
        isClientExperimentLoading
      }
    >
      {label}
    </SignUpButtonBase>
  );
}

export function SignUpButton(props: SignUpButtonProps) {
  const { isUserAuthenticated } = useUser();
  const clientRender = useClientRender();
  const label = props.overrideLabel ?? 'Enroll Now';

  if (!clientRender) {
    return (
      <Button {...buttonStyleProps} isDisabled isLoading>
        {label}
      </Button>
    );
  }

  return isUserAuthenticated ? <AuthenticatedSignUpButton {...props} /> : <UnauthenticatedSignUpButton {...props} />;
}

export function IndividualSignUpButton(props: Omit<SignUpButtonProps, 'planId'>) {
  return <SignUpButton {...props} planId='individual' />;
}

export function TeamSignUpButton(props: Omit<SignUpButtonProps, 'planId'>) {
  function handleClick() {
    props?.onClick?.();
    trackEvent({
      name: 'Plans Sign Up Clicked',
      type: 'click',
      label: 'Team',
    });
  }
  return <SignUpButton {...props} planId='team' onClick={handleClick} />;
}
